/**
 * Wire shapes for the Reports page's "Sync Dropbox" flow — the self-service
 * face of the legacy Dropbox import (docs/ROADMAP.md: Dropbox is a read-only
 * legacy import path; these types retire with the adapter).
 *
 * The flow is two endpoints so each Lambda invocation stays well inside the
 * 30-second timeout: `candidates` is a cheap store-only listing, and `import`
 * processes exactly ONE job per call (Dropbox search + folder listing +
 * checklist parse + photo downloads). The admin UI loops the import call over
 * the candidate list and renders per-job outcomes as they arrive.
 */
import { z } from 'zod';
import { AccreditationCodeSchema, InspectionTypeSchema } from './cer-portal.js';
import { JobStatusSchema } from './jobs.js';
/** One job the sync button may try to import: booked or in-field, no report yet. */
export const DropboxSyncCandidateSchema = z.object({
    jobId: AccreditationCodeSchema,
    status: JobStatusSchema,
    inspectionType: InspectionTypeSchema,
    inspectorId: z.string().optional(),
    scheduledDate: z.string().optional(),
});
export const DropboxSyncCandidatesResponseSchema = z.object({
    candidates: z.array(DropboxSyncCandidateSchema),
});
/**
 * Outcome of one import attempt.
 *
 *  - `imported`: a pending report now exists and the job moved to `submitted`.
 *  - `not-ready`: the inspection hasn't happened yet as far as Dropbox can
 *    tell (no job folder, an "Upcoming Folders" placement, or a future-dated
 *    day folder). Nothing written. Thin folder CONTENTS are not a blocker —
 *    a job with no checklist or photos imports with warnings on the report's
 *    outstanding list instead (warn, don't block; the portal is the final
 *    completeness check).
 *  - `skipped`: the job no longer qualifies (status moved on, or a report
 *    appeared since the candidate list was fetched). Nothing written.
 */
export const DropboxSyncOutcomeSchema = z.enum(['imported', 'not-ready', 'skipped']);
export const DropboxSyncImportResultSchema = z.object({
    jobId: AccreditationCodeSchema,
    outcome: DropboxSyncOutcomeSchema,
    /** Human-readable reason — always present for not-ready / skipped. */
    reason: z.string().optional(),
    /** Present when `outcome` is `imported`. */
    summary: z
        .object({
        submittedAt: z.string(),
        folderPath: z.string(),
        /** Filename of the filled checklist PDF the clauses came from, when one parsed. */
        checklistPdf: z.string().optional(),
        clauses: z.number().int().nonnegative(),
        nonCompliant: z.number().int().nonnegative(),
        /** Clauses present in the PDF but left unticked by the inspector. */
        unanswered: z.number().int().nonnegative(),
        photos: z.number().int().nonnegative(),
    })
        .optional(),
});
//# sourceMappingURL=dropbox-sync.js.map